import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";
import { api } from "../../lib/api";
import { resolveImageUrl } from "../admin/ImageUpload";

const FALLBACK = [
  {
    quote:
      "GESIL transformed our revenue collection across 30+ ULBs in record time. The Municipal Revenue Augmentation Platform is now a benchmark for urban governance in Odisha.",
    name: "Senior Officer",
    designation: "Housing & Urban Development Department",
    organization: "Government of Odisha",
    photo_url: "",
    rating: 5,
  },
  {
    quote:
      "The CTGST State API Repository unified GST intelligence for the Commercial Tax department. GESIL's engineering and PMU rigor stood out throughout the rollout.",
    name: "Project Lead",
    designation: "Commissionerate of CT & GST",
    organization: "Government of Odisha",
    photo_url: "",
    rating: 5,
  },
  {
    quote:
      "UniDAF AI Platform has set the standard for sovereign, on-premise AI in government. Our analysts now save hours every week.",
    name: "Innovation Head",
    designation: "Odisha AI Mission",
    organization: "Government of Odisha",
    photo_url: "",
    rating: 5,
  },
];

const initial = (name) => (name || "?").trim().charAt(0).toUpperCase();

export default function TestimonialsSection() {
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    api
      .get("/testimonials")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setItems(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="testimonials"
      data-testid="section-testimonials"
      className="relative bg-white dark:bg-[#0B1B40] py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute -top-32 left-0 h-72 w-72 rounded-full bg-blue-200/30 dark:bg-blue-900/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 right-0 h-72 w-72 rounded-full bg-[#FF9933]/20 dark:bg-[#FF9933]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="section-overline" style={{ color: "#FF9933" }}>
            Testimonials
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold mt-5 leading-tight text-balance text-[#0F172A] dark:text-white">
            Trusted by governments and PSUs across India.
          </h2>
          <p className="mt-5 text-[#475569] dark:text-blue-100 text-lg leading-relaxed">
            What our partner departments say about working with GESIL.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <motion.figure
              key={t.id || `${t.name}-${i}`}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className="group relative bg-white dark:bg-[#0F172A] rounded-3xl border border-blue-100 dark:border-blue-800 hover:border-[#FF9933] p-7 md:p-8 shadow-[0_18px_40px_-26px_rgba(15,23,42,0.18)] hover:shadow-[0_32px_70px_-34px_rgba(255,153,51,0.4)] transition-all flex flex-col"
            >
              <Quote
                size={28}
                className="text-[#FF9933] mb-4"
                strokeWidth={1.5}
              />
              <blockquote className="text-[#0F172A] dark:text-blue-50 text-[15px] leading-relaxed italic flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>

              {t.rating > 0 && (
                <div className="mt-5 flex gap-0.5" aria-label={`${t.rating} out of 5 stars`}>
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={14} className={s < (t.rating || 5) ? "text-[#FF9933] fill-[#FF9933]" : "text-blue-200 dark:text-blue-800"} />
                  ))}
                </div>
              )}

              <figcaption className="mt-5 pt-5 border-t border-blue-100 dark:border-blue-800 flex items-center gap-4">
                {t.photo_url ? (
                  <img
                    src={resolveImageUrl(t.photo_url)}
                    alt={t.name}
                    loading="lazy"
                    className="h-14 w-14 rounded-full object-cover border-2 border-blue-100 dark:border-blue-800 shrink-0"
                  />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] text-white font-display text-xl flex items-center justify-center shrink-0">
                    {initial(t.name)}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-display text-lg text-[#0F172A] dark:text-white leading-tight truncate">
                    {t.name}
                  </div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#475569] dark:text-blue-200 mt-1 line-clamp-1">
                    {t.designation}
                  </div>
                  {t.organization && (
                    <div className="text-[11px] text-[#2563EB] dark:text-cyan-300 font-semibold mt-0.5 line-clamp-1">
                      {t.organization}
                    </div>
                  )}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
